/**
 * RulesList - List view of categorization rules (WP-CAT-12)
 *
 * Displays rules in a list with:
 * - Name and pattern
 * - Pattern type badge
 * - Target category
 * - Priority and confidence
 * - Active toggle
 * - Edit/Delete actions for custom rules
 */
import { cn } from '@/utils/cn';
import ToggleSwitch from '@/components/ui/ToggleSwitch';
import Button from '@/components/ui/Button';
import { useCategoryPreferences } from '@/hooks/useCategoryPreferences';
import { PATTERN_TYPE_CONFIG, type CategorizationRule } from '@/types/categorizationRule';

// ============================================================================
// Props
// ============================================================================

export interface RulesListProps {
  rules: CategorizationRule[];
  onEdit: (rule: CategorizationRule) => void;
  onDelete: (ruleId: string) => void;
  onToggleActive: (rule: CategorizationRule) => void;
  isUpdating?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export default function RulesList({
  rules,
  onEdit,
  onDelete,
  onToggleActive,
  isUpdating = false,
}: RulesListProps) {
  // Get categories for displaying target category name
  const { data: categories } = useCategoryPreferences(true);

  // Helper to get category name
  const getCategoryName = (categoryId: string): string => {
    const category = categories?.find((c) => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  // Sort rules by priority (highest first)
  const sortedRules = [...rules].sort((a, b) => b.priority - a.priority);

  return (
    <div className="divide-y divide-gray-200">
      {sortedRules.map((rule) => {
        const patternConfig = PATTERN_TYPE_CONFIG[rule.patternType];

        return (
          <div
            key={rule.id}
            className={cn(
              'py-4 px-2 hover:bg-gray-50 transition-colors',
              !rule.isActive && 'opacity-60'
            )}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Rule info */}
              <div className="flex-1 min-w-0">
                {/* Name and badges */}
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">{rule.name}</span>
                  {rule.isSystem && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      System
                    </span>
                  )}
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {patternConfig.label}
                  </span>
                </div>

                {/* Pattern */}
                <div className="text-sm text-gray-600 font-mono truncate mb-1">
                  <span className="text-gray-400">Pattern:</span> {rule.pattern}
                </div>

                {/* Target category and priority */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span>
                    <span className="text-gray-400">Category:</span>{' '}
                    <span className="text-gray-700">{getCategoryName(rule.categoryId)}</span>
                  </span>
                  <span>
                    <span className="text-gray-400">Priority:</span>{' '}
                    <span className="text-gray-700">{rule.priority}</span>
                  </span>
                  <span>
                    <span className="text-gray-400">Confidence:</span>{' '}
                    <span className="text-gray-700">{(rule.confidence * 100).toFixed(0)}%</span>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {/* Active toggle */}
                <ToggleSwitch
                  id={`rule-${rule.id}-active`}
                  label={`${rule.isActive ? 'Active' : 'Inactive'}`}
                  enabled={rule.isActive}
                  onChange={() => onToggleActive(rule)}
                  isLoading={isUpdating}
                  disabled={rule.isSystem}
                />

                {/* Edit button (custom rules only) */}
                {!rule.isSystem && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(rule)}
                    aria-label={`Edit ${rule.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </Button>
                )}

                {/* Delete button (custom rules only) */}
                {!rule.isSystem && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(rule.id)}
                    aria-label={`Delete ${rule.name}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
